<template>
  <div class="ex-checkpoint-edit-menu">
    <CheckpointBanner
      v-if="instructionsBannerEnabled && instructions === 'add' && !isAddingInstructions"
      missing_component="Instructions"
      @add-click="handleBannerAddClick"
    />
    <CheckpointBanner
      v-if="microlearning === 'add' && !hasMicrolearningVideo"
      missing_component="Microlearning"
      @add-click="handleBannerAddClick"
    />
    <CheckpointBanner
      v-if="quizz === 'add' && show_quiz"
      missing_component="Quiz"
      @add-click="handleBannerAddClick"
    />
    <v-row v-if="hasSelectedItems" class="mt-1 org-hori-menu">
      <button
        v-for="(item, index) in menuItems"
        :key="index"
        :class="['menu-btn pointer', { 'active-btn': selectedTab === item.label }]"
        @click="selectTab(item)"
        text
      >
        <div class="menu-item">
          <!-- Icon as URL/string (default) -->
          <img
            v-if="isStringIcon(item.icon)"
            :src="item.icon"
            alt="icon"
            :class="['icon', { 'icon-active': selectedTab === item.label }, 'mr-2']"
          />
          <!-- Inline SVG markup support -->
          <span
            v-else-if="item.iconSvg"
            class="icon-svg mr-2"
            v-html="item.iconSvg"
            :class="{ 'icon-active': selectedTab === item.label }"
          />
          <!-- Icon as component (advanced) -->
          <component
            v-else-if="item.iconComponent"
            :is="item.iconComponent"
            :class="['icon', { 'icon-active': selectedTab === item.label }, 'mr-2']"
          />
          <span class="item-label">{{ item.label }}</span>
        </div>
      </button>
    </v-row>
    <!-- Tab Content Area -->
    <div v-if="hasSelectedItems" class="tab-content">
      <!-- Instructions Tab Content -->
      <CheckpointFormInstructionsTabEditable
        v-if="selectedTab === instructionsTabLabel"
        :current="isAddingInstructions ? { content: '' } : instructionsContent"
        :current_is_published="isAddingInstructions ? false : instructionsIsPublished"
        :change="isAddingInstructions ? null : instructionsChange"
        :checkpointId="checkpointId"
        :responsibilityId="responsibilityId"
        :checkpointCriteria="checkpointCriteria"
        :checkpointPrefix="checkpointPrefix"
        :checkpointLevel="checkpointLevel"
        :checkpointStatus="checkpointStatus"
        @publish="handleInstructionsPublish"
        @discard="handleInstructionsDiscard"
        @propose-changes="$emit('instructions-propose-changes')"
        @input="$emit('instructions-input', $event)"
        @instructions-updated="$emit('instructions-updated', $event)"
        :isStorybook="isStorybook"
      />

      <!-- Microlearning Tab Content -->
      <div v-else-if="selectedTab === 'Microlearning'" class="microlearning-content">
        <CheckpointMicrolearningVideoCard
          :videoUrl="microlearningVideoUrl"
          :videoId="microlearningVideoId"
          :checkpointId="checkpointId"
          :responsibilityId="responsibilityId"
          :checkpointPrefix="checkpointPrefix"
          :checkpointCriteria="checkpointCriteria"
          :responsibilityCode="responsibilityCode"
          :responsibilityName="responsibilityName"
          :responsibilityPurpose="responsibilityPurpose"
          :checkpointInstructions="checkpointInstructions"
          :microlearningInstructions="microlearningInstructions"
          :set-to-microlearning-instructions="set_to_microlearning_instructions"
          @replace="handleReplaceVideo"
          @delete="handleDeleteVideo"
          @video-play="handleVideoPlay"
          @video-pause="handleVideoPause"
          @video-ended="handleVideoEnded"
          @refresh-checkpoint="$emit('refresh-checkpoint')"
        />
      </div>

      <!-- Quiz Tab Content - placeholder for now -->
      <div v-else-if="selectedTab === 'Quiz'" class="quiz-content">
        <!-- Quiz content will go here -->
      </div>
      <div
        v-else-if="selectedTab === 'Video Instructions' && props.set_to_microlearning_instructions"
        class="video-instructions-content"
      >
        <MicrolearningVideoActionsBar
          v-if="!isVideoProcessing"
          :disabled="isVideoProcessing"
          :hasInstructions="hasMicrolearningInstructions"
          :isVideoInstructions="set_to_microlearning_instructions"
          @replace="handleReplaceVideo"
          @delete="handleDeleteVideo"
        />
        <MicrolearningViewEdit
          :video="parsedMicrolearningInstructions.video_url || microlearningVideoUrl"
          :error="parsedMicrolearningInstructions.error || null"
          :summary="parsedMicrolearningInstructions.summary || null"
          :steps="parsedMicrolearningInstructions.steps || []"
          :editable="false"
          :compact-steps-layout="true"
        />
      </div>
    </div>

    <!-- Upload Microlearning Popup -->
    <UploadMicroLearningPopup
      v-model="showUploadDialog"
      @uploaded="handleVideoUploaded"
      :responsibilityId="responsibilityId"
      :checkpointId="checkpointId"
      @video-url-ready="$emit('video-url-ready', $event)"
      @refresh-checkpoint="$emit('refresh-checkpoint')"
    />

    <!-- Delete Confirmation Dialog -->
    <DeleteMicrolearning
      v-model="showDeleteDialog"
      @onDelete="confirmDelete"
      @input="showDeleteDialog = false"
    />
  </div>
</template>

<script setup>
import CheckpointBanner from "@components/CheckpointBanner/CheckpointBanner.vue";
import CheckpointFormInstructionsTabEditable from "@components/CheckpointFormInstructionsTabEditable/CheckpointFormInstructionsTabEditable.vue";
import UploadMicroLearningPopup from "@components/micro-learning/UploadMicroLearningPopup.vue";
import CheckpointMicrolearningVideoCard from "@components/CheckpointMicrolearningVideoCard/CheckpointMicrolearningVideoCard.vue";
import MicrolearningViewEdit from "@components/MicrolearningViewEdit/MicrolearningViewEdit.vue";
import MicrolearningVideoActionsBar from "@components/MicrolearningVideoActionsBar/MicrolearningVideoActionsBar.vue";
import DeleteMicrolearning from "@components/RolesCatalog/DeleteMicrolearning.vue";
import { ref, watch, computed, getCurrentInstance, onMounted } from "vue";
import { deleteProposedVideo, changeCheckpointV2 } from "@/api";
import { LD_FLAG_INSTRUCTIONS_BANNER_IN_CHECKPOINT_EDIT_FORM } from "@components/constants/launchDarklyFlagKeys";

// Emits
const emit = defineEmits([
  "instructions-publish",
  "instructions-discard",
  "instructions-propose-changes",
  "instructions-input",
  "instructions-updated",
  "upload-microlearning",
  "video-uploaded",
  "video-url-ready",
  "video-deleted",
  "video-play",
  "video-pause",
  "video-ended",
  "refresh-checkpoint",
]);

// Get Vue instance for $loader and $notify
const app = getCurrentInstance();
const { proxy } = app || {};

// Props definition
const props = defineProps({
  instructions: {
    type: String,
    default: "add",
  },
  // Instructions content data for CheckpointFormInstructionsTabEditable
  instructionsContent: {
    type: Object,
    default: null,
  },
  instructionsIsPublished: {
    type: Boolean,
    default: false,
  },
  instructionsChange: {
    type: Object,
    default: null,
  },
  microlearning: {
    type: String,
    default: "add",
  },
  // Microlearning video URL (when video exists)
  microlearningVideoUrl: {
    type: String,
    default: null,
  },
  // Microlearning video ID (for delete/manage operations)
  microlearningVideoId: {
    type: [String, Number],
    default: null,
  },
  // Checkpoint ID for upload (stored in localStorage by parent)
  checkpointId: {
    type: [String, Number],
    default: null,
  },
  // Responsibility ID for API calls
  responsibilityId: {
    type: [String, Number],
    default: null,
  },
  // Checkpoint data needed for API payload
  checkpointCriteria: {
    type: String,
    default: "",
  },
  checkpointPrefix: {
    type: String,
    default: "",
  },
  checkpointLevel: {
    type: [Number, String],
    default: 1,
  },
  checkpointStatus: {
    type: String,
    default: "active",
  },
  quizz: {
    type: String,
    default: "add",
  },
  show_quiz: {
    type: Boolean,
    default: false,
  },
  // Responsibility data for IQ prompt
  responsibilityCode: {
    type: String,
    default: "",
  },
  responsibilityName: {
    type: String,
    default: "",
  },
  responsibilityPurpose: {
    type: String,
    default: "",
  },
  // Checkpoint instructions for IQ prompt (business_purpose)
  checkpointInstructions: {
    type: String,
    default: "",
  },
  microlearningInstructions: {
    type: String,
    default: "",
  },
  isStorybook: {
    type: Boolean,
    default: false,
  },
  set_to_microlearning_instructions: {
    type: Boolean,
    default: false,
  },
});

// State for upload dialog
const showUploadDialog = ref(false);

// State for delete dialog
const showDeleteDialog = ref(false);

// State for adding instructions mode (when user clicks "Add Instructions" on banner)
const isAddingInstructions = ref(false);

// LD flag: show instructions banner + tab in checkpoint edit form
const instructionsBannerEnabled = ref(false);

onMounted(async () => {
  try {
    instructionsBannerEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_INSTRUCTIONS_BANNER_IN_CHECKPOINT_EDIT_FORM, false)) === true;
  } catch {
    instructionsBannerEnabled.value = false;
  }
});

const instructionsTabLabel = computed(() => {
  return props.instructions === "video" ? "Video Instructions" : "Instructions";
});

// Computed to check if microlearning video exists (used for banner display)
const hasMicrolearningVideo = computed(() => {
  return props.microlearningVideoUrl && props.microlearningVideoUrl.length > 0;
});

// Parse microlearning instructions - handles both string (JSON) and object
const parsedMicrolearningInstructions = computed(() => {
  if (!props.microlearningInstructions) return {};
  const instructions = props.microlearningInstructions;
  // If it's a string, try to parse as JSON
  if (typeof instructions === "string") {
    try {
      return JSON.parse(instructions);
    } catch (e) {
      return {};
    }
  }
  // If it's already an object, return as-is
  if (typeof instructions === "object" && instructions !== null) {
    return instructions;
  }
  return {};
});

// Check if microlearning instructions have meaningful content
const hasMicrolearningInstructions = computed(() => {
  const instructions = parsedMicrolearningInstructions.value;
  if (Object.keys(instructions).length > 0) {
    return !!(instructions.steps?.length > 0 || instructions.summary);
  }
  return false;
});

// Check if video is processing
const isVideoProcessing = computed(() => {
  if (!props.microlearningVideoUrl) return false;
  return (
    props.microlearningVideoUrl.includes("being processed") ||
    props.microlearningVideoUrl.includes("will be available in a moment") ||
    props.microlearningVideoUrl.includes("processing")
  );
});

const handleBannerAddClick = (componentType) => {
  if (componentType === "Instructions") {
    isAddingInstructions.value = true;
    selectedTab.value = instructionsTabLabel.value;
  } else if (componentType === "Microlearning") {
    if (props.checkpointId) {
      localStorage.setItem("selectedChecklist", JSON.stringify(props.checkpointId));
    }
    showUploadDialog.value = true;
  }
  emit("upload-microlearning", componentType);
};

const handleVideoUploaded = async (payload) => {
  // If set_to_microlearning_instructions is true, clear both instructions and microlearning_instructions
  if (props.set_to_microlearning_instructions) {
    try {
      proxy.$loader.setAppLoading(true);
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      if (accountId && props.checkpointId && props.responsibilityId) {
        const clearPayload = {
          instructions: null,
          microlearning_instructions: null,
          set_to_microlearning_instructions: false,
        };
        await changeCheckpointV2(
          accountId,
          props.responsibilityId,
          props.checkpointId,
          clearPayload
        );
        emit("refresh-checkpoint");
      }
    } catch (error) {
      console.error("Error clearing instructions on video upload:", error);
    } finally {
      proxy.$loader.setAppLoading(false);
    }
  } else {
    // If set_to_microlearning_instructions is false, just clear microlearning_instructions
    try {
      proxy.$loader.setAppLoading(true);
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      if (accountId && props.checkpointId && props.responsibilityId) {
        const clearPayload = {
          microlearning_instructions: null,
        };
        await changeCheckpointV2(
          accountId,
          props.responsibilityId,
          props.checkpointId,
          clearPayload
        );
        emit("refresh-checkpoint");
      }
    } catch (error) {
      console.error("Error clearing microlearning instructions on video upload:", error);
    } finally {
      proxy.$loader.setAppLoading(false);
    }
  }

  emit("video-uploaded", payload);
  showUploadDialog.value = false;
};

// Handler to replace video - opens the upload dialog
const handleReplaceVideo = () => {
  // Store checkpoint ID in localStorage for the upload component
  if (props.checkpointId) {
    localStorage.setItem("selectedChecklist", JSON.stringify(props.checkpointId));
  }
  showUploadDialog.value = true;
};

// Handler to delete video - shows confirmation dialog
const handleDeleteVideo = () => {
  // Show confirmation dialog instead of directly deleting
  showDeleteDialog.value = true;
};

// Confirm delete - performs the actual deletion
const confirmDelete = async () => {
  if (!props.checkpointId) {
    console.error("Missing checkpoint ID for deletion");
    return;
  }

  try {
    proxy.$loader.setAppLoading(true);

    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

    // Delete the proposed video first
    await deleteProposedVideo(accountId, props.checkpointId);

    // If set_to_microlearning_instructions is true (Video Instructions tab), clear both instructions and microlearning_instructions
    if (props.set_to_microlearning_instructions) {
      const payload = {
        instructions: null,
        microlearning_instructions: null,
        set_to_microlearning_instructions: false,
      };
      await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, payload);
    } else {
      // If set_to_microlearning_instructions is false, just clear microlearning_instructions
      const payload = {
        microlearning_instructions: null,
      };
      await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, payload);
    }

    showDeleteDialog.value = false;
    // Emit event to parent to refresh checkpoint data
    emit("video-deleted");
    emit("refresh-checkpoint");
  } catch (error) {
    console.error("Error deleting microlearning video:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

// Instructions event handlers
const handleInstructionsPublish = (content) => {
  // After publishing, exit adding mode
  isAddingInstructions.value = false;
  emit("instructions-publish", content);
};

const handleInstructionsDiscard = () => {
  // If in adding mode, just exit without saving
  if (isAddingInstructions.value) {
    isAddingInstructions.value = false;
    selectedTab.value = menuItems.value.length > 0 ? menuItems.value[0].label : "";
  }
  emit("instructions-discard");
};

// Video player event handlers
const handleVideoPlay = () => {
  emit("video-play");
};

const handleVideoPause = () => {
  emit("video-pause");
};

const handleVideoEnded = () => {
  emit("video-ended");
};

const menuItems = computed(() => {
  const items = [];

  // Check if instructions and microlearning_instruction are identical
  const instructionsIdentical = props.set_to_microlearning_instructions;

  // If both are identical, show only "Video Instructions" with instruction icon
  if (instructionsIdentical) {
    if (
      props.instructions === "selected" ||
      props.instructions === "not selected" ||
      props.instructions === "video" ||
      isAddingInstructions.value ||
      props.microlearning === "selected" ||
      props.microlearning === "not selected"
    ) {
      items.push({
        label: "Video Instructions",
        iconSvg:
          '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 13.3333V10M10 6.66667H10.0083M6.5 17.5H13.5C14.9001 17.5 15.6002 17.5 16.135 17.2275C16.6054 16.9878 16.9878 16.6054 17.2275 16.135C17.5 15.6002 17.5 14.9001 17.5 13.5V6.5C17.5 5.09987 17.5 4.3998 17.2275 3.86502C16.9878 3.39462 16.6054 3.01217 16.135 2.77248C15.6002 2.5 14.9001 2.5 13.5 2.5H6.5C5.09987 2.5 4.3998 2.5 3.86502 2.77248C3.39462 3.01217 3.01217 3.39462 2.77248 3.86502C2.5 4.3998 2.5 5.09987 2.5 6.5V13.5C2.5 14.9001 2.5 15.6002 2.77248 16.135C3.01217 16.6054 3.39462 16.9878 3.86502 17.2275C4.3998 17.5 5.09987 17.5 6.5 17.5Z" stroke="#344054" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      });
    }
  } else {
    // If not identical, show both separately (current behavior)
    // Show Instructions tab if: already has content OR user clicked "Add Instructions"
    if (
      instructionsBannerEnabled.value &&
      (props.instructions === "selected" ||
        props.instructions === "not selected" ||
        props.instructions === "video" ||
        isAddingInstructions.value)
    ) {
      items.push({
        label: instructionsTabLabel.value,
        iconSvg:
          '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 13.3333V10M10 6.66667H10.0083M6.5 17.5H13.5C14.9001 17.5 15.6002 17.5 16.135 17.2275C16.6054 16.9878 16.9878 16.6054 17.2275 16.135C17.5 15.6002 17.5 14.9001 17.5 13.5V6.5C17.5 5.09987 17.5 4.3998 17.2275 3.86502C16.9878 3.39462 16.6054 3.01217 16.135 2.77248C15.6002 2.5 14.9001 2.5 13.5 2.5H6.5C5.09987 2.5 4.3998 2.5 3.86502 2.77248C3.39462 3.01217 3.01217 3.39462 2.77248 3.86502C2.5 4.3998 2.5 5.09987 2.5 6.5V13.5C2.5 14.9001 2.5 15.6002 2.77248 16.135C3.01217 16.6054 3.39462 16.9878 3.86502 17.2275C4.3998 17.5 5.09987 17.5 6.5 17.5Z" stroke="#344054" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      });
    }
    // When instructions are "video", we intentionally suppress the Microlearning menu
    // so the user sees "Video Instructions" + "Quiz" only (per design).
    if (
      props.instructions !== "video" &&
      (props.microlearning === "selected" || props.microlearning === "not selected")
    ) {
      items.push({
        label: "Microlearning",
        iconSvg:
          '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.91667 7.47111C7.91667 7.07337 7.91667 6.8745 7.99978 6.76348C8.07222 6.66673 8.18309 6.6062 8.30365 6.59759C8.44199 6.58771 8.60927 6.69524 8.94384 6.91032L12.8777 9.43921C13.168 9.62585 13.3131 9.71917 13.3633 9.83783C13.4071 9.9415 13.4071 10.0585 13.3633 10.1622C13.3131 10.2808 13.168 10.3742 12.8777 10.5608L8.94384 13.0897C8.60927 13.3048 8.44199 13.4123 8.30365 13.4024C8.18309 13.3938 8.07222 13.3333 7.99978 13.2365C7.91667 13.1255 7.91667 12.9266 7.91667 12.5289V7.47111Z" stroke="#344054" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.5 6.5C2.5 5.09987 2.5 4.3998 2.77248 3.86502C3.01217 3.39462 3.39462 3.01217 3.86502 2.77248C4.3998 2.5 5.09987 2.5 6.5 2.5H13.5C14.9001 2.5 15.6002 2.5 16.135 2.77248C16.6054 3.01217 16.9878 3.39462 17.2275 3.86502C17.5 4.3998 17.5 5.09987 17.5 6.5V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V6.5Z" stroke="#344054" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      });
    }
  }

  if (props.quizz === "selected" || props.quizz === "not selected") {
    items.push({
      label: "Quiz",
      iconSvg:
        '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.25 6.04102H10.625" stroke="#666666" stroke-width="2.25" stroke-linecap="round"/><path d="M1.25 14.791H10.625" stroke="#666666" stroke-width="2.25" stroke-linecap="round"/><path d="M13.75 5.76919L15.0977 7.40216C15.1406 7.45419 15.2194 7.45699 15.2659 7.40814L18.75 3.75" stroke="#666666" stroke-width="1.5" stroke-linecap="round"/><path d="M18.2715 12.3711L14.343 16.2013" stroke="#666666" stroke-width="1.5" stroke-linecap="round"/><path d="M14.3923 12.3203L18.2225 16.2488" stroke="#666666" stroke-width="1.5" stroke-linecap="round"/></svg>',
    });
  }
  return items;
});

const hasSelectedItems = computed(() => menuItems.value.length > 0);

const selectedTab = ref("");

const initializeSelectedTab = () => {
  const selectedItem = menuItems.value.find((item) => {
    // Handle "Video Instructions" when instructions are identical
    if (props.set_to_microlearning_instructions && item.label === "Video Instructions") {
      if (
        props.instructions === "selected" ||
        props.microlearning === "selected" ||
        isAddingInstructions.value
      )
        return true;
    }
    if (
      item.label === instructionsTabLabel.value &&
      (props.instructions === "selected" || isAddingInstructions.value)
    )
      return true;
    if (item.label === "Microlearning" && props.microlearning === "selected") return true;
    if (item.label === "Quiz" && props.quizz === "selected") return true;
    return false;
  });

  if (selectedItem) {
    selectedTab.value = selectedItem.label;
  } else if (menuItems.value.length > 0) {
    selectedTab.value = menuItems.value[0].label;
  } else {
    selectedTab.value = "";
  }
};

watch(
  menuItems,
  () => {
    initializeSelectedTab();
  },
  { immediate: true }
);

watch(props, () => {
  initializeSelectedTab();
});

const isStringIcon = (val) => {
  return typeof val === "string" && val.length > 0;
};

const selectTab = (item) => {
  selectedTab.value = item.label;
};

defineExpose({
  selectedTab,
  selectTab,
  showUploadDialog,
  isAddingInstructions,
});
</script>

<style scoped>
.ex-checkpoint-edit-menu {
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.ex-checkpoint-edit-menu .org-hori-menu {
  margin-left: 0px;
  border-bottom: 1px solid #eaecf0;
}
.ex-checkpoint-edit-menu .org-hori-menu .icon {
  width: 24px;
  height: 24px;
  filter: grayscale(100%); /* Default to gray */
  transition: filter 0.3s ease;
  margin-top: -2px;
}
.ex-checkpoint-edit-menu .org-hori-menu .icon-active {
  filter: brightness(0) saturate(100%) invert(43%) sepia(93%) saturate(2467%) hue-rotate(227deg)
    brightness(88%) contrast(88%);
}
/* Inline SVG styling to mirror the <img> icon behavior */
.ex-checkpoint-edit-menu .org-hori-menu .icon-svg {
  display: inline-flex;
}
.ex-checkpoint-edit-menu .org-hori-menu .icon-svg svg {
  width: 24px;
  height: 24px;
  filter: grayscale(100%);
  transition: filter 0.3s ease;
}
.ex-checkpoint-edit-menu .org-hori-menu .icon-svg.icon-active svg {
  filter: brightness(0) saturate(100%) invert(43%) sepia(93%) saturate(2467%) hue-rotate(227deg)
    brightness(88%) contrast(88%);
}
.ex-checkpoint-edit-menu .org-hori-menu .menu-btn {
  margin: 6px 0px 12px 0px;
  min-height: 36px;
  padding: 4px 12px;
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  border: none;
  background: none;
  color: #475467;
  cursor: pointer;
  transition: all 0.3s ease;
}
.ex-checkpoint-edit-menu .org-hori-menu .active-btn {
  color: #7f56d9;
  border-bottom: 2px solid #d6bbfb;
}
.ex-checkpoint-edit-menu .org-hori-menu .menu-item {
  display: flex;
  align-items: center;
  white-space: nowrap;
}
.ex-checkpoint-edit-menu .org-hori-menu .item-label {
  font-weight: 500;
}
.ex-checkpoint-edit-menu .org-hori-menu .custom-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  margin-left: 8px;
  border-radius: 50%;
  background-color: #f9fafb;
  border: 1px solid #eaecf0;
  color: #344054;
  font-size: 12px;
}
.ex-checkpoint-edit-menu .tab-content {
  margin-top: 16px;
}

/* Microlearning Content Container */
.microlearning-content {
  width: 100%;
}

/* Video Instructions Content Container */
.video-instructions-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Add border to step cards when compactStepsLayout is true */
.video-instructions-content
  :deep(.microlearning-view-edit--compact .microlearning-view-edit__step-card) {
  border: 1px solid #eaecf0;
  border-radius: 8px;
  padding: 12px;
}
</style>
